import { useState, useEffect, useMemo } from "react"
import ReactDOM from "react-dom"
import { get, post } from "../../../../utils/apiClient"
import { formatFaxNumber } from "../../../../utils/common"
import toast from "react-hot-toast"
import Pagination from "../../../common/pagination"
import PopupFaxNumberSelector from "./popupFaxNumberSelector"

const FaxRow = ({
  practice,
  saveLoading,
  handleAssign,
  hoveredRow,
  activeRow,
  setHoveredRow,
  setActiveRow,
  selectedApp,
  allAssignedFaxNumbers,
  authUser,
  setMoreInfo,
}) => {
  const selectedValues = useMemo(() => {
    const faxNumbers = practice.faxNumberAttributes
    return faxNumbers.map((fn) => ({
      value: fn.fax_number,
      label: `${fn?.last_name}`,
    }))
  }, [practice.faxNumberAttributes])

  const selectedValuesString = selectedValues
    .map((selectedValue) => selectedValue.label)
    .join(", ")

  const selectedTitleString = selectedValues
    .map((selectedValue) => selectedValue.value)
    .join(", ")

  const selectedValuesStringMaxThree =
    selectedValues.length > 3
      ? selectedValues
          .slice(0, 3)
          .map((selectedValue) => selectedValue.label)
          .join(", ") + " ... "
      : selectedValues.map((selectedValue) => selectedValue.label).join(", ")

  return (
    <tr
      key={practice.practiceId}
      className={`${hoveredRow === practice.practiceId || activeRow === practice.practiceId ? "row-highlighted" : ""}`}
      onMouseEnter={() => {
        setHoveredRow(practice.practiceId)
        if (activeRow !== practice.practiceId) setActiveRow(null)
      }}
      onMouseLeave={() => setHoveredRow(null)}
    >
      <td className="faxes-td" style={{ width: "200px" }}>
        {practice.practiceName}
      </td>
      <td className="faxes-td" style={{ width: "200px" }}>
        <span
          style={{
            maxWidth: "100px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={selectedTitleString}
        >
          {selectedValuesStringMaxThree}
        </span>
        {selectedValues.length > 3 && (
          <span
            style={{ cursor: "pointer", color: "blue" }}
            onClick={() =>
              setMoreInfo({
                practiceName: practice.practiceName,
                faxNumberAttributes: practice.faxNumberAttributes,
              })
            }
          >
            More
          </span>
        )}
      </td>
      <td className="faxes-td" style={{ textAlign: "center" }}>
        {saveLoading === practice.practiceId ? (
          "Saving ..."
        ) : (
          <PopupFaxNumberSelector
            popupType="FAX_NUMBER_ASSIGNMENT"
            key={practice.emailAddress}
            appId={selectedApp}
            allAssignedFaxNumbers={allAssignedFaxNumbers}
            selectedValues={selectedValues}
            onApply={(selectedOptions) => {
              handleAssign(practice.practiceId, selectedOptions)
            }}
            linkLabel="Assign"
            title={`Assign Fax Numbers to ${practice.practiceName}`}
            saveLoading={saveLoading}
            authUser={authUser}
          />
        )}
      </td>
    </tr>
  )
}

const FaxNumberAssignment = ({ authUser }) => {
  const [practices, setPractices] = useState([])
  const [selectedApp, setSelectedApp] = useState(null)
  const [allAssignedFaxNumbers, setAllAssignedFaxNumbers] = useState([])
  const [appLoading, setAppLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [moreInfo, setMoreInfo] = useState(null)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [activeRow, setActiveRow] = useState(null)

  // Search Filter
  const filteredPractices = practices?.filter((practice) => {
    return Object.entries(practice).some(([key, value]) => {
      if (key === "practiceName") {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.trim().toLowerCase())
      }
      return false
    })
  })

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentPractices = filteredPractices.slice(
    startIdx,
    startIdx + rowsPerPage,
  )
  const totalPages = Math.ceil(filteredPractices.length / rowsPerPage)

  const fetchApps = async () => {
    setAppLoading(true)
    try {
      const applications = await get("/admin/applications")
      const appConfiguredApps = applications.filter(
        (application) => application.appConfigured === true,
      )
      setSelectedApp(appConfiguredApps[0].app_id) // Currently considering a single application
      setAppLoading(false)
    } catch (error) {
      console.error("Failed to fetch apps", error)
    } finally {
      setAppLoading(false)
    }
  }

  const fetchPractices = async () => {
    try {
      // set applications
      const practices = await get("/practice/practicesWithFaxNumbers")
      setPractices(practices)

      const allFaxNumbers = practices.flatMap((practice) =>
        practice.faxNumberAttributes.map((attr) => attr.fax_number),
      )
      setAllAssignedFaxNumbers(allFaxNumbers)
      // set practices
    } catch (error) {
      console.error("Failed to fetch apps", error)
    } finally {
      setAppLoading(false)
    }
  }

  const loadPractices = async () => {
    setAppLoading(true)
    fetchPractices()
  }

  useEffect(() => {
    fetchApps()
    loadPractices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAssign = async (practiceId, selectedOptions) => {
    if (!selectedOptions) return

    setSaveLoading(practiceId)

    try {
      await post("/practice/assign-faxNumbers", {
        practiceId: practiceId,
        faxNumberAttributes: selectedOptions.map((o) => o?.faxNumberAttributes),
      })

      toast.success("Changes saved successfully", {
        duration: 5000,
      })

      await fetchPractices()
      setSaveLoading(null)
    } catch (error) {
      console.error("Failed to assign users", error)
      toast.error(error, {
        duration: 5000,
      })
      setSaveLoading(null)
    }
  }

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {appLoading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <>
          <div
            className="faxes-content"
            style={{
              textAlign: "center",
              width: "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              className="search-container"
              style={{
                width: "100%",
                alignItems: "end",
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "5px",
              }}
            >
              <div className="setting-note"></div>
              <div style={{ flexShrink: 0 }}>
                {selectedApp && (
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder={`Search Pratice...`}
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value)
                        setCurrentPage(1)
                      }}
                      style={{ width: "200px", paddingRight: "22px" }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSearchText("")
                        setCurrentPage(1)
                      }}
                      className="search-clear-btn"
                      style={{ visibility: searchText ? "visible" : "hidden" }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>

            {selectedApp && (
              <>
                <div className="table-container">
                  <table className="faxes-table">
                    <thead>
                      <tr>
                        <th className="faxes-th" style={{ width: "30%" }}>
                          Practices
                        </th>
                        <th className="faxes-th" style={{ width: "70%" }}>
                          Fax Numbers
                        </th>
                        <th
                          className="faxes-th"
                          style={{ textAlign: "center" }}
                        >
                          Action
                        </th>
                        {/* <th
                              className="faxes-th"
                              style={{ textAlign: "center" }}
                            >
                              Action
                            </th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {currentPractices.length > 0 ? (
                        currentPractices.map((practice) => (
                          <FaxRow
                            key={practice.practiceId}
                            practice={practice}
                            saveLoading={saveLoading}
                            handleAssign={handleAssign}
                            hoveredRow={hoveredRow}
                            activeRow={activeRow}
                            setHoveredRow={setHoveredRow}
                            setActiveRow={setActiveRow}
                            selectedApp={selectedApp}
                            allAssignedFaxNumbers={allAssignedFaxNumbers}
                            authUser={authUser}
                            setMoreInfo={setMoreInfo}
                          />
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="100%"
                            style={{
                              textAlign: "center",
                              padding: "10px",
                              border: "1px solid #ccc",
                            }}
                          >
                            No data found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {filteredPractices.length > 0 && (
                  <div
                    className="faxes-pagination"
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    {filteredPractices.length > 0 && (
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        totalRows={filteredPractices.length}
                        onPageChange={(page) => setCurrentPage(page)}
                        onRowsPerPageChange={(count) => {
                          setRowsPerPage(count)
                          setCurrentPage(1) // Reset to first page on change
                        }}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
      {moreInfo && (
        <InfoPopup moreInfo={moreInfo} onCancel={() => setMoreInfo(null)} />
      )}
    </div>
  )
}

export default FaxNumberAssignment

const InfoPopup = ({ moreInfo, onCancel }) =>
  ReactDOM.createPortal(
    <div className="popup-overlay">
      <div className="popup" style={{ maxWidth: "600px" }}>
        <div className="checkbox-popup-header">
          <div className="popup-title" style={{ fontSize: "14px" }}>
            {moreInfo.practiceName}
          </div>
          <button
            type="button"
            onClick={onCancel}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            &times;
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <table className="faxes-table" style={{ width: "100%" }}>
            {/* <thead>
              <tr>
                <th className="faxes-th">Last Name</th>
                <th className="faxes-th">Fax Number</th>
              </tr>
            </thead> */}
            <tbody>
              {moreInfo.faxNumberAttributes.map((fn) => (
                <tr key={fn.fax_number}>
                  <td className="faxes-td">{fn.last_name}</td>
                  <td className="faxes-td">{formatFaxNumber(fn.fax_number)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  )
